import * as React from "react";
import { CheckoutPage } from '../checkout-page.component';
import { useState, useEffect } from "react";
import { DealerInfo } from "./dealer-info";
import { ProductReference } from "./product-reference";
import { DeliveryAddressPicker } from "./delivery-address-picker";
import { Summary } from "../summary";
import { Button } from "@montana/shared/button";
import { MobileAndTabletPortrait } from "@montana/shared/responsive";
import { useCart } from '@montana/containers/cart';
import { ICheckoutPage } from "@montana/models/generated/cms.interface";
import { useAuthentication } from "@montana/containers/authentication";
import { Translate } from "@montana/shared/translate";
import { formatString } from "@montana/helpers/string-format/string-format.helper";
import { useTranslations } from "@montana/containers/translations";
import { useRouter } from "@montana/containers/router";
import { useTracking } from "@montana/containers/tracking";
import { DynamicBlocks } from "@montana/blocks/dynamic-blocks.component";
import { useDynamicSettings } from "@montana/containers/dynamic-settings";

export const BillingPage: React.FC<ICheckoutPage> = ({ topBlocks, bottomBlocks }: ICheckoutPage): JSX.Element => {
    const [index, setIndex] = useState(0);
    const [productReferenceVal, setProductReferenceVal] = useState<string>("");
    const { user } = useAuthentication();
    const { links } = useDynamicSettings();
    const [deliveryAddress, setDeliveryAddress] = useState<any | undefined>(user?.deliveryAddresses && user.deliveryAddresses[0]);

    const { push } = useRouter();

    const {
        placeOrder,
        placeOrderLoading,
        linesToDisplay: linesToDisplay,
    } = useCart();

    const { trackCheckoutStep } = useTracking();

    useEffect(() => {
        trackCheckoutStep(2);
    }, [])

    useEffect(() => {
        setTimeout(() => setIndex(1), 100);
    }, [])

    useEffect(() => {
        if (user?.deliveryAddresses) {
            // Set default deliveryAddress, should user load after this component mounts.
            setDeliveryAddress(user.deliveryAddresses[0]);
        }
    }, [user])

    useEffect(() => {
        if (!linesToDisplay?.length) {
            push(links?.cart || "/");
        }
    }, [])

    const translate = useTranslations();

    const translatedTexts = {
        summaryHeading: translate((translations): string => translations.checkoutPage.summary, "Summary"),
    };

    function handleCheckout(): void {
        placeOrder(
            deliveryAddress,
            productReferenceVal,
        );
    }

    return (
        <CheckoutPage progressBarIndex={index}>
            <section className="billing-page">

                <DynamicBlocks blocks={topBlocks} />

                <MobileAndTabletPortrait>
                    <Button className="billing-page__place-order-button-mobile" onClick={handleCheckout} isLoading={placeOrderLoading} disabled={!user?.deliveryAddresses?.length}>
                        <Translate value={(t): string => t.checkoutPage.placeYourOrder} defaultValue="Place order"></Translate>
                    </Button>
                </MobileAndTabletPortrait>

                <div className="billing-page__header">
                    <h1 className="billing-page__title display-5">
                        <Translate value={(t): string => t.checkoutPage.progressBarAddressAndDelivery} defaultValue="Address & Delivery"></Translate>
                    </h1>
                    <hr className="billing-page__header-hr border--extra-light" />
                </div>

                <div className="billing-page__content-row row dynamic-content-below">
                    <div className="billing-page__main col">
                        <div className="billing-page__info-row row">
                            <div className="billing-page__info-col billing-page__info-col--dealer col">
                                <DealerInfo />
                            </div>
                            <div className="billing-page__info-col col">
                                <ProductReference value={productReferenceVal} onChange={setProductReferenceVal} />
                            </div>
                        </div>

                        <hr className="billing-page__separator border--extra-light" />

                        {user && (
                            <DeliveryAddressPicker
                                currentAddress={deliveryAddress}
                                setCurrentAddress={setDeliveryAddress}
                                addresses={user.deliveryAddresses}
                            />
                        )}
                    </div>

                    <div className="billing-page__aside col">
                        <div className="billing-page__summary-wrapper">
                            <Summary header={translatedTexts.summaryHeading} />
                        </div>
                        <Button className="billing-page__place-order-button" onClick={handleCheckout} isLoading={placeOrderLoading} disabled={!user?.deliveryAddresses?.length}>
                            <Translate value={(t): string => t.checkoutPage.placeYourOrder} defaultValue="Place order"></Translate>
                        </Button>
                        {user?.email &&
                            <p className="billing-page__confirmation-text">
                                <Translate value={(t): string => t.checkoutPage.emailConfirmation}>{(t): string => formatString(t, `${user.email}`)}</Translate>
                            </p>
                        }
                    </div>
                </div>

                <DynamicBlocks blocks={bottomBlocks} />

            </section>
        </CheckoutPage >
    );
};
